import {Tr} from "@/i18n/i18n";
import TextUtils, {FontsKeys, TextAlign} from "@/utils/text-utils";
import {Keys} from "@/constants/key";
import {ChainTaskInfo, Task, TaskType} from "@/store/task/types";
import {SquareButton} from "@/scenes.ui/square-button";
import ResourceUtils from "@/utils/resource-utils";
import {MenuNewElementMarker} from "@/scenes.ui/menu-new-element-marker";
import OnceCaller from "@/utils/once-caller";
import {Link} from "@/scenes.ui/link";
import ShopStore from "@/store/shop";
import {ShopCreature} from "@/store/shop/types";
import {CreatureDescPanel} from "@/scenes.ui/creature-desc-panel";
import {TaskConstants} from "@/constants/task-constants";
import TaskStore from "@/store/task";
import SpriteUtils from "@/utils/sprite-utils";
import TimeUtils from "@/utils/time-utils";
import CU from "../utils/c-u";
import PlayerStore from "@/store/player";
import TaskUtils from "@/utils/task-utils";
import {StringUtils} from "@/utils/string-utils";
import PlayerUtils from "@/utils/player-utils";
import {Gender} from "@/model/gender";
import GameObject = Phaser.GameObjects.GameObject;
import Image = Phaser.GameObjects.Image;
import BitmapText = Phaser.GameObjects.BitmapText;

export class TaskPanel extends Phaser.GameObjects.Container {

    public static HEIGHT = 189;
    private readonly COMPLETE_BUTTON_POS_X: number = 515;
    private readonly PROGRESS_VALUES_DISTANCE: number = 10;
    private readonly INNER_BAR_WIDTH: number = 294;
    private readonly MAIN_TITLE_FONT_SIZE: number = 18;
    private readonly INFO_FONT_SIZE: number = 14;
    private readonly INFO_FONT_STYLE: string = 'bold';

    private innerBar!: Image;
    private taskTypeRibbon!: Image;
    private taskTypeText!: BitmapText;
    
    private taskTypeBack!: Image;

    private completeButton!: SquareButton;

    private static PROCESSING_TASK_IDS: Set<number> = new Set<number>();

    private awardReceivedText!: BitmapText;

    constructor(scene: Phaser.Scene, canvas: HTMLCanvasElement, t: Task, callback: () => void, openRatingCallback: () => void) {
        super(scene);

        this.scene = scene;
        this.width = CU.CANVAS_WIDTH;
        this.height = TaskPanel.HEIGHT;

        const back = SpriteUtils.addImage(this.scene, 0, 0, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_PANEL_BACK).setOrigin(0, 0);
        SpriteUtils.setDispSize(back, CU.CANVAS_WIDTH, TaskPanel.HEIGHT * CU.S);

        this.add(back);

        this.taskTypeBack = SpriteUtils.addImage(this.scene, 0, -4, Keys.ATLAS_PROFILE, Keys.UI_SCENARIO_TASK_BACK).setOrigin(0, 0).setVisible(false);
        SpriteUtils.setDispSize(this.taskTypeBack, CU.CANVAS_WIDTH, TaskPanel.HEIGHT * CU.S);
        this.taskTypeRibbon =  SpriteUtils.addImage(this.scene!, 0, 22, Keys.ATLAS_UI, Keys.UI_SCENARIO_RIBBON)
            .setOrigin(0, 0.5).setVisible(false);

        this.taskTypeText = TextUtils.addBMText(scene, Tr('stsenarnoe'), this.taskTypeRibbon.x / CU.S + 60,
         this.taskTypeRibbon.y / CU.S - 8, 11.8, 320, TextAlign.CENTER, 1, FontsKeys.COINY).setVisible(false);
        this.taskTypeText.setTintFill(0xffffff);

        this.add([this.taskTypeBack, this.taskTypeRibbon, this.taskTypeText]);

        this.makeRibbon(t);

        let titleText = TextUtils.addBMText(scene, t.name.toUpperCase(), 112, 43, 17, 480,
         TextAlign.LEFT, 2, FontsKeys.COINY);

        let remainMillis = t.optionalEndsAt? new Date(t.optionalEndsAt).getTime()- new Date().getTime() : -1;
        let remainMillisText = remainMillis > 0? TimeUtils.getDurationTextSimple(remainMillis/1000).toLowerCase() : "";

        let timeLeft = TextUtils.addBMText(scene, remainMillisText, 500, 150, 15.8, 320,
        TextAlign.LEFT, 1, FontsKeys.COINY).setOrigin(0, 0);
        timeLeft.setTintFill(0x706f6b);

        if(remainMillis <= 0 || t.awardReceived || t.progress >= t.total){
            timeLeft.visible = false;
        }

        let titleFlash = SpriteUtils.addImage(this.scene, 270, titleText.y / CU.S - 1, Keys.ATLAS_UI, Keys.UI_TASK_TITLE_FLASH);

        let rewardMap = ResourceUtils.convertResForTask(t.gold, t.crystal, t.elixir, t.mithril, 0, t.tickets);

        let rewardItems: GameObject[] = [];
        
        let rewardTxt = TextUtils.addBMText(scene, Tr('nagrada').toUpperCase() + ': ', titleText.x / CU.S, titleText.y / CU.S + 27, 15.8, 320,
            TextAlign.LEFT, 1, FontsKeys.COINY).setOrigin(0, 0);
            rewardTxt.setTintFill(0x706f6b);

        rewardItems.push(rewardTxt);
        let posX = rewardTxt.getRightCenter().x! / CU.S + this.PROGRESS_VALUES_DISTANCE;
        let posY = rewardTxt.y / CU.S;
        for (let [resType, resValue] of rewardMap) {
            
            let countText = TextUtils.addBMText(scene, TextUtils.formatMoney(resValue), posX, posY, 15.8, 320,
            TextAlign.LEFT, 1, FontsKeys.COINY).setOrigin(0, 0);
            countText.setTintFill(0xc9c9c9);

            rewardItems.push(countText);
            posX = countText.getRightCenter().x! / CU.S + 1

            let countIcon = SpriteUtils.addSprite(this.scene!, posX,  posY - 10, ResourceUtils.getResourceImageNoShadow(resType))
                .setOrigin(0, 0);
            SpriteUtils.setDispSize(countIcon, 34 * CU.S, 34 * CU.S);

            rewardItems.push(countIcon);
            posX = countIcon.getRightCenter().x! / CU.S + 8
        }

        let progressText = TextUtils.addBMText(scene, Tr('vipolneno'), rewardTxt.x / CU.S, rewardTxt.y / CU.S + 30, 15.8, 320,
        TextAlign.LEFT, 1, FontsKeys.COINY).setOrigin(0, 0);
        progressText.setTintFill(0x706f6b);

        let taskProgress = TextUtils.addBMText(scene, TextUtils.formatMoney(t.progress) + '/' + TextUtils.formatMoney(t.total),
        progressText.getRightCenter().x! / CU.S + this.PROGRESS_VALUES_DISTANCE, progressText.y / CU.S - 1, 15.8, 320,
        TextAlign.LEFT, 1, FontsKeys.COINY).setOrigin(0, 0);
        taskProgress.setTintFill(0xc9c9c9);

        const separator = SpriteUtils.addSprite(this.scene!, 0, TaskPanel.HEIGHT, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_SEPARATOR).setOrigin(0, 1);

        let imDesc: ImDesc = TaskPanel.getImageDescriptor(t.taskId);

        const image = !!imDesc.atlasId ? SpriteUtils.addImage(this.scene, 60, 85, imDesc.atlasId, imDesc.imageId) : SpriteUtils.addImage(this.scene, 60, 85, imDesc.imageId);
        image.setOrigin(0.5, 0.5);
        if (image.getBounds().height > 90) {
            SpriteUtils.scale(image, 90 / image.getBounds().height);
        }

        let outerBar = SpriteUtils.addImage(this.scene, progressText.x / CU.S - 3, progressText.y / CU.S + 30, Keys.ATLAS_UI, Keys.UI_TASK_PROGRESS_BAR_OUTER)
            .setOrigin(0, 0);

        this.innerBar = SpriteUtils.addImage(this.scene, outerBar.x / CU.S + 3, outerBar.y / CU.S + 4, Keys.ATLAS_UI, Keys.UI_TASK_PROGRESS_BAR_INNER)
            .setOrigin(0, 0);
        SpriteUtils.setDispSize(this.innerBar, this.INNER_BAR_WIDTH * (t.progress / t.total) * CU.S, 22 * CU.S);

        let taskDone = t.progress >= t.total;
        this.completeButton = SpriteUtils.scale(new SquareButton(this.scene, this.COMPLETE_BUTTON_POS_X, TaskPanel.HEIGHT / 2 + 13, -20, Keys.UI_ART_GREEN_BUTTON,
            Keys.ATLAS_PROFILE, Keys.UI_GOLDEN_CHECK, '', false, 84 * CU.S, 84 * CU.S, 0), 0.9 / CU.S)
            .setVisible(false);

        //*** for event ***
        let ratingButtonVisible = TaskUtils.ALL_EVENT_TASKS_WITH_PRIZES_PANEL.includes(t.taskId);

        let ratingButton = SpriteUtils.addSprite(this.scene, this.completeButton.x / CU.S, this.completeButton.y / CU.S + 30, Keys.ATLAS_UI, Keys.UI_BUTTON_BROWN_SMALL, 0.8, 0.6);
        ratingButton.alpha = 0.5;
        ratingButton.setVisible(ratingButtonVisible);
        ratingButton.setInteractive().on('pointerup', () => {
            openRatingCallback();
        });

        let ratingText = TextUtils.addBMText(scene, Tr('prizy').toUpperCase(), this.completeButton.x / CU.S, this.completeButton.y / CU.S + 30, 16, 100,
            TextAlign.CENTER, 1, FontsKeys.COINY)
        ratingText.setTintFill(0x725946);
        ratingText.setVisible(ratingButtonVisible);

        let yourRatingText = TextUtils.addBMText(scene, "", this.completeButton.x / CU.S, this.completeButton.y / CU.S - 35, 16, 100,
            TextAlign.CENTER, 1, FontsKeys.COINY)
        yourRatingText.setTintFill(0x706f6b);
        yourRatingText.setVisible(ratingButtonVisible);

        TaskStore.fetchRatingPositionByEvent().then((position: number) => {
            yourRatingText.setText(StringUtils.formatString(Tr('vi.na.{0}.meste').toUpperCase(), position));
        });

        //**********

        let completeText = TextUtils.addBMText(scene, Tr('poluchit.nagradu').toUpperCase(), this.completeButton.x / CU.S, this.completeButton.y / CU.S + 30, 14.8, 100,
        TextAlign.CENTER, 1, FontsKeys.COINY).setVisible(false);
        completeText.setTintFill(0xffffff);

        this.awardReceivedText = TextUtils.addBMText(scene, Tr('nagrada.poluchena').toLowerCase(),
        this.completeButton.x / CU.S, this.completeButton.y / CU.S, 13.8, 100,
        TextAlign.CENTER, 1, FontsKeys.COINY).setVisible(false);
        this.awardReceivedText.setTintFill(0xffffff);

        this.completeButton.setCallback(() => {
            this.completeButton.setVisible(false);
            completeText.setVisible(false);
            OnceCaller.doOnce(String(t.id), () => callback());
            this.makeCallbackPropertyByTaskType(t);
        });

        this.add([titleFlash, titleText, timeLeft, image, progressText, taskProgress, ...rewardItems, this.innerBar, outerBar,
            this.completeButton, completeText,  this.awardReceivedText, separator, ratingButton, ratingText, yourRatingText]);

        if (t.heroId && t.heroId.length > 0) {
            let heroText = TextUtils.addBMText(scene, Tr('mythical.hero'), posX - 1, posY + 12, 15.8, 320,
            TextAlign.LEFT, 1, FontsKeys.COINY);
            heroText.setTintFill(0xffffff);

            let link = new Link(scene, heroText.getRightCenter().x! + 6 * CU.S, posY * CU.S + 3 * CU.S, Tr('det'), () => {
                ShopStore.fetchCreatureById(t.heroId!).then((sc: ShopCreature) => {
                    let cdp = new CreatureDescPanel(scene, canvas, sc);
                    cdp.openAnimated();
                });
            }, '#b4b4b4', 12);

            this.add([heroText, link]);
        }

        if (t.taskId === TaskConstants.TASK_EASTER_EGG //|| t.taskId === TaskConstants.TASK_FERN
            || t.taskId === TaskConstants.TASK_APPLE || t.taskId === TaskConstants.TASK_BOOKS
            || t.taskId === TaskConstants.TASK_PUMPKIN) {// || t.taskId === TaskConstants.TASK_BIRTHDAY_HAT) {

            let ctiText = TextUtils.addBMText(scene, '', taskProgress.getRightCenter().x! / CU.S + 15, taskProgress.y / CU.S + 3,
             13.8, 320, TextAlign.LEFT, 1, FontsKeys.COINY).setOrigin(0, 0);
             ctiText.setTintFill(0x706f6b);

            this.add([ctiText]);

            TaskStore.fetchChainTaskInfo(t.taskId).then((cti: ChainTaskInfo) => {
                ctiText.setText(String(cti.counter) + ' ' + Tr('sst') + ', ' + String(cti.place) + ' ' + Tr('plc'));
            });
        }

        if (t.taskNew) {
            let taskNewMarker = new MenuNewElementMarker(scene, 515, 15, Tr('novoe'));
            this.add(taskNewMarker);
        }

        if (t.awardReceived) {
            this.completeButton.setVisible(false);
            this.awardReceivedText.setVisible(true);

            ratingButton.setVisible(ratingButtonVisible);
            ratingText.setVisible(ratingButtonVisible);
            yourRatingText.setVisible(ratingButtonVisible);
            return;
        }

        if (taskDone) {
            this.completeButton.setVisible(true);
            completeText.setVisible(true);

            ratingButton.setVisible(false);
            ratingText.setVisible(false);
            yourRatingText.setVisible(false);
        }
    }

    public static isTaskIdProcessing(t: Task): boolean {
        return TaskPanel.PROCESSING_TASK_IDS.has(t.id);
    }

    private static getImageDescriptor(taskId: string): ImDesc {
        switch (taskId) {
            case 'task-gold':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_100_GOLD);
            case 'task-crystal':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_100_CRYSTAL);
            case 'task-elixir':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_100_ELIXIR);
            case 'task-mithril':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_100_MITHRIL);
            case 'task-flower-1':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_FLOWERS_1);
            case 'task-mushroom-1':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_MUSHROOM_1);
            case 'task-battle':
                return new ImDesc(undefined, Keys.IMG_VICTORY_KNIGHT);
            case 'task-victory':
                return new ImDesc(undefined, Keys.IMG_VICTORY_KNIGHT);
            case 'task-daily-battle':
                return new ImDesc(undefined, Keys.IMG_VICTORY_KNIGHT);
            case 'task-art-improve':
                return new ImDesc(Keys.ATLAS_PROFILE, Keys.UI_ART_UPPER_ICON);
            case 'task-art-find':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_LOUPE);
            case 'task-buy-gems':
            case 'task-buy-gems-count':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_100_MITHRIL);
            case 'task-find-rabbit':
            case 'kill_rabbit_rain':
                return new ImDesc(Keys.ATLAS_PLACES_1, 'white-rabbit');
            case 'hole-and-egg':
            case 'ng_hole-and-egg':
                return new ImDesc(Keys.ATLAS_PLACES_ZONES_2, 'dragon_big_1');
            case 'rabbit-and-chest':
            case 'ng_rabbit-and-chest':
            case 'rabbit_sled':
            case 'carrot_big':
            case 'rab_again':
                return new ImDesc(Keys.ATLAS_PLACES_1, 'white-rabbit');
            case 'find-seven-keys':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_100_KEY1);
            case 'collect-pumpkin':
                return new ImDesc(Keys.ATLAS_PLACES_1, 'pumpkin_1');
            case 'find-piggy':
                return new ImDesc(Keys.ATLAS_PLACES_1, 'piggy');
            case 'find-key-keeper':
                return new ImDesc(Keys.ATLAS_PLACES_1, 'keeper-hut');
            case 'collect-bright-crystals':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_BRIGHT_CRYSTAL);
            case 'find-statue-parts':
                return new ImDesc(Keys.ATLAS_PLACES_1, 'statue-body');
            case 'find-statue-again':
                return new ImDesc(Keys.ATLAS_PLACES_1, 'statue-head');
            case 'statue-1-tribute':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_STATUE_1_TRIBUTE);
            case 'statue-2-tribute':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_STATUE_2_TRIBUTE);
            case 'statue-3-tribute':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_STATUE_3_TRIBUTE);
            case 'statue-4-tribute':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_STATUE_4_TRIBUTE);
            case 'statue-5-tribute':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_STATUE_5_TRIBUTE);
            case 'statue-6-tribute':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_STATUE_6_TRIBUTE);
            case 'collect-mandra':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_MANDRAHORA);
            case 'return-dragon-egg':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_DRAGON_EGG);
            case 'find_tranduil':
            case 'returnToTranduil':
            case 'findElfTranduil':
            case 'tranduil-last':
            case 'to_ektilion':
            case 'to_ektelion':
            case 'to_maglor':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_ELF_TRANDUIL);
            case 'find-five-crystals':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_RUNECRYSTAL_META);
            case 'findSpaceCrystal':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_RUNECRYSTAL_1);
            case 'findPortal':
            case 'portal-return':
            case 'p3_to_rain_world':
            case 'to_native_world':
            case 'death_bridge':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_PORTAL);
            case 'find-necklace':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_NECKLACE_1);
            case 'find-billy':
            case 'bring-billy-pie':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_BILLY_NO_SHADE);
            case 'collect-fresh-meat':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_FRESH_MEAT_1);
            case 'make-pie-for-billy':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_MEAT_PIE);
            case 'kill-minotaur':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_MINOTAUR);
            case 'bring-bernis-necklace':
            case 'bring-fresh-meat':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_BERNIS_NO_SHADE);
            case 'collect-gloombloom':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_GLOOMBLOOM);
            case 'collect-doombloom':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_DOOMBLOOM);
            case 'bring-gloombloom':
            case 'bring-doombloom':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_BALTUS_NO_SHADE);
            case 'kill-samuel':
                return new ImDesc(Keys.ATLAS_CREATURES_ROAD, '9-necro-3');
            case 'bring-samuels-remain':
                return new ImDesc(Keys.ATLAS_PLACES_1, 'constance-grave');
            case 'task-participate-on-arena':
                return new ImDesc(undefined, Keys.IC_BR_CUP);
            case 'sand_look_around':
                return new ImDesc(Keys.ATLAS_PLACES_2, 'iniquity_ruins');
            case 'find_elder':
            case 'elder_return':
            case 'elder_return_2':
            case 'elder_return_3':
            case 'elder_return_4':
            case 'elder_return_5':
            case 'elder_return_6':
            case 'elder_return_13':
            case 'elder_return_14':
            case 'p3_meet_elder':
                return new ImDesc(Keys.ATLAS_PLACES_2, 'elder_tent');
            case 'find_potion_keeper':
            case 'potion_keeper4_return':
                return new ImDesc(Keys.ATLAS_PLACES_2, 'keeper_tent');
            case 'kill_seth_ent':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_100_ATTACK);
            case 'collect-kukaryamba':
                return new ImDesc(Keys.ATLAS_GAINABLE_2, 'collect-kukaryamba');
            case 'hiding_potion_task':
                return new ImDesc(Keys.ATLAS_PLACES_2, 'rainbow-potion');
            case 'dragon_orden':
            case 'dragon_orden_2':
                return new ImDesc(Keys.ATLAS_PLACES_2, 'dragon_mountains');
            case 'meet_petya':
                return new ImDesc(Keys.ATLAS_PLACES_2, 'iniquity_ruins');
            case 'meet_library_man':
            case 'gvindor':
                return new ImDesc(Keys.ATLAS_PLACES_2, 'library');
            case 'find_fellow':
                return new ImDesc(Keys.ATLAS_PLACES_2, 'fellow_mine');
            case 'forbideen_temple_task':
            case 'forb_temple_2':
            case 'to_panteon':
            case 'to_castle':
            case 'to_time_tree':
                return new ImDesc(Keys.ATLAS_PLACES_2, 'forbidden_temple');
            case 'kill_hydra':
                return new ImDesc(Keys.ATLAS_CREATURES_BATTLE_2, 'p2_sp-06');
            case 'nefrit_crystals':
                return new ImDesc(Keys.ATLAS_PLACES_2, 'c_green');
            case 'coral_crystals':
                return new ImDesc(Keys.ATLAS_PLACES_2, 'c_red');
            case 'blue_crystals':
                return new ImDesc(Keys.ATLAS_PLACES_2, 'c_blue');
            case 'return_to_the_past':
                return new ImDesc(Keys.ATLAS_PROFILE, 'exit_door');
            case 'kill_sigi':
                return new ImDesc(Keys.ATLAS_CREATURES_BATTLE_2, 'p2_m116-gin');
            case 'dragon_tulip':
                return new ImDesc(Keys.ATLAS_GAINABLE_2, 'dragon_tulip');
            case 'volcano_crystal':
                return new ImDesc(Keys.ATLAS_GAINABLE_2, 'volcano_crystal');
            case 'shadow_berry':
                return new ImDesc(Keys.ATLAS_GAINABLE_2, 'shadow_berry');
            case 'meetPedro':
                return new ImDesc(Keys.ATLAS_PLACES_2, 'pedro_house');
            case 'deliver_mail':
                return new ImDesc(Keys.ATLAS_PROFILE, Keys.UI_PROFILE_MAIL);
            case 'kill-swb-task':
                return new ImDesc(Keys.ATLAS_CREATURES_ROAD_4, 'swb-01');
            case 'shad_crystals':
                return new ImDesc('atlas_places_7', 'myststone');
            case 'ghost_address':
                return new ImDesc('atlas_places_7', 'pillar');
            case 'life_bush':
                return new ImDesc(undefined, 'life_bush');
            case 'chickens':
                return new ImDesc(undefined, 'chickens');
            case 'moon_crystals':
                return new ImDesc('atlas_places_7', 'moonstone');
            case 'mirmaid_tail':
                return new ImDesc(undefined, 'mirmaid_tail');
            case 'kill-yot-task':
                return new ImDesc('atlas_places_7', 'yot-12');
            case 'silver_pieces':
                return new ImDesc('atlas_places_7', 'silver');
            case 'win_rk_task':
                return new ImDesc('atlas_places_7', 'rokkara');
            case 'win_ns_task':
                return new ImDesc('atlas_places_7', 'ns_dark');
            case 'sdq-bird1':
                return new ImDesc(undefined, 'sdq-bird1');
            case 'sdq-bird2':
                return new ImDesc(undefined, 'sdq-bird2');
            case 'sdq-bird3':
                return new ImDesc(undefined, 'sdq-bird3');
            case 'sdq-black-stone':
                return new ImDesc(undefined, 'sdq-black-stone');
            case 'collect_map_parts':
                return new ImDesc(undefined, 'collect_map_parts');
            case 'find_cavirals':
                return new ImDesc(undefined, 'find_cavirals');
            case 'kill_barbarians_full':
                return new ImDesc("places-nps", 'np_barbarian_1');
            case 'most_faster':
            case 'unstopable':
                return new ImDesc(undefined, Keys.IMG_VICTORY_KNIGHT);
            case 'kill_defilers':
                return new ImDesc('atlas_places_12', 'p6_statue_need_help');
            case 'find_saint_rock_for_statue':
                return new ImDesc('atlas_places_12', 'p6_saint_rock_for_statue');
            case 'sdq-flower1':
                return new ImDesc(undefined, 'sdq-flower1');
            case 'sdq-flower3':
                return new ImDesc(undefined, 'sdq-flower3');
            case 'sdq-stem-floral':
                return new ImDesc(undefined, 'sdq-stem-floral');


            case 'findRamina':
            case 'to_ramina':
            case 'to_maya':
                return new ImDesc(Keys.ATLAS_PLACES_1, 'p-sister1');
            case 'findGektor':
            case 'to_gektor2':
                return new ImDesc(Keys.ATLAS_PLACES_3, 'gektor');
            case 'findBelmo':
            case 'returnToBlind':
                return new ImDesc(Keys.ATLAS_PLACES_3, 'blind_older');
            case 'pine_doom':
                return new ImDesc(Keys.ATLAS_GAINABLE_2, 'pine_doom');
            case 'findOldTent':
                return new ImDesc(Keys.ATLAS_PLACES_1, 'tent');
            case 'returnToMing':
            case 'to_ming4':
                return new ImDesc(Keys.ATLAS_PLACES_3, 'ming');
            case 'yellow_crystals':
                return new ImDesc(Keys.ATLAS_PLACES_2, 'c_yellow');
            case 'orange_crystals':
                return new ImDesc(Keys.ATLAS_PLACES_2, 'c_orange');
            case 'fate_roots':
                return new ImDesc(Keys.ATLAS_GAINABLE_2, 'fate_roots');
            case 'to_gvindi':
                return new ImDesc(Keys.ATLAS_PLACES_1, 'gvindor');
            case 'sulfur':
                return new ImDesc(Keys.ATLAS_GAINABLE_2, 'sulfur');
            case 'p3_keys':
                return new ImDesc(Keys.ATLAS_GAINABLE_2, 'p3_keys');
            case 'sdq-fint-tent': return new ImDesc('atlas_places_1','tent');
            case 'naitiOsheinik': return new ImDesc('places-sdq', 'belt');
            case 'continueSearch': return new ImDesc('atlas_places_7','vill_run_3');
            case 'naitiLogovo': return new ImDesc('places-sdq','burrow');
            case 'speakwithprisoner': return new ImDesc('places-sdq','man-in-cage');
            case 'backwithheadofvillage': return new ImDesc('places-sdq','fat-man');
            case 'killslizen': return new ImDesc('atlas_cr_road','16-beast-9' );
            case 'killheroicvelikan': return new ImDesc('atlas_cr_road_4','p4_m319-demon');
            case 'killheroicgolems': return new ImDesc('places-sdq','golems');
            case 'kill_golems_frog_wq5': return new ImDesc('places-sdq','golems');
            case 'killheroictroll': return new ImDesc('atlas_cr_road','10-troll-1');
            case 'badcurse': return new ImDesc('atlas_places_1','p-sister2');
            case 'findredzhivitsa': return new ImDesc('places-sdq','witch');
            case 'collect_golem_blood_wq4': return new ImDesc('places-sdq','vial');
            case 'kill_velikan_wq4': return new ImDesc('places-sdq','hobbit-house');
            case 'collect_rings_of_nature_wq6': return new ImDesc('places-sdq','ring3');
            case 'redozherelie': return new ImDesc('places-sdq','collar');
            case 'bonefordog': return new ImDesc('places-sdq','bone');
            case 'findpashtet': return new ImDesc('places-sdq','jar');
            case 'findkifir': return new ImDesc('places-sdq','fliaga');

            case TaskConstants.TASK_EASTER_EGG:
                return new ImDesc(Keys.ATLAS_GAINABLE_2, 'task-easter-egg');
            case TaskConstants.TASK_BIRTHDAY_HAT:
                if(PlayerStore.getPlayer()!.scenarioMarkers.some(m => m.scenarioMarker == "birthday-for-queen")){
                    return new ImDesc(undefined, Keys.TASK_BIRTHDAY_QUEEN);
                } else {
                    return new ImDesc(undefined, Keys.TASK_BIRTHDAY_DRONT);
                }
            case TaskConstants.TASK_FERN:
                return new ImDesc(undefined, 'task-fern');
            case TaskConstants.TASK_APPLE:
                return new ImDesc(undefined, 'task-apple');
            case TaskConstants.TASK_BOOKS:
                return new ImDesc(undefined, 'task-books');
            case TaskConstants.TASK_PUMPKIN:
                return new ImDesc(undefined, 'task-pumpkin');
            case 'five_tablets':
                return new ImDesc(Keys.ATLAS_PLACES_11, 'scroll');
            case 'seven_keepers':
                return new ImDesc(Keys.ATLAS_PLACES_11, 'keeper_1');
            case 'three_priests':
                return new ImDesc(Keys.ATLAS_PLACES_11, 'priestess_1');
            case 'd_jonny':
                let gender = PlayerUtils.getGender(PlayerStore.getPlayer());
                return new ImDesc(Keys.ATLAS_PLACES_11, gender === Gender.male ? 'dark_johnny_t' : 'dark_alice_t');
            case 'find_amul':
                return new ImDesc(Keys.ATLAS_PLACES_11, 'amulet');
            case 'kill_shadows':
                return new ImDesc(Keys.ATLAS_CREATURES_ROAD_8, 'p6_shadow_3');
            case 'seven_sphinxes':
                return new ImDesc(Keys.ATLAS_PLACES_11, 'gor');
            case 'atlant_tablet':
                return new ImDesc(Keys.ATLAS_PORTALS, 'P3');
            case 'cows':
                return new ImDesc(Keys.ATLAS_PLACES_13, 'p13_cow_fallout_1');
            case 'invis':
                return new ImDesc(Keys.ATLAS_PLACES_13, 'p13_invisible_guy_2');
            case 'find_wise':
                return new ImDesc('', 'wise_man');
            case 'find_thor_chest':
                return new ImDesc(Keys.ATLAS_PLACES_1, 'thor_chest');
            case 'golem':
                return new ImDesc(Keys.ATLAS_PLACES_1, 'golem_1');
            case 'ice_golem':
                return new ImDesc(Keys.ATLAS_PLACES_1, 'golem_2');
            case 'dark_golem':
                return new ImDesc(Keys.ATLAS_PLACES_1, 'golem_3');
            case 'learn_scroll':
                return new ImDesc(Keys.ATLAS_PLACES_1, 'asgard_scroll');
            case 'p8_kill_arch':
                return new ImDesc(Keys.ATLAS_PLACES_15, 'arch');
            case 'p8_back_to_ml':
            case 'p8_to_miro':
            case 'norn_world':
                return new ImDesc(Keys.ATLAS_IC_100, Keys.IC_PORTAL);
            case 'p8_to_source':
                return new ImDesc(Keys.ATLAS_POINTERS_8, 'p8_pointer_water');
            case 'meet_dns':
                return new ImDesc(Keys.ATLAS_PLACES_14, 'dark_serra_hood');
            case 'p8_loki':
                return new ImDesc(Keys.ATLAS_PLACES_15, 'loki');
            case 'black_matter_1':
            case 'black_matter_2':
            case 'black_matter_3':
            case 'black_matter_4':
                return new ImDesc(undefined, 'black_matter_1');
            case 'find_exile':
            case 'exile_curse':
                return new ImDesc(Keys.ATLAS_PLACES_14, 'exile');
            case 'find_ancient':
                return new ImDesc(Keys.ATLAS_PLACES_14, 'ancient');
            case 'find_faina':
                return new ImDesc(Keys.ATLAS_PLACES_14, 'mar');
            case 'find_liam':
                return new ImDesc(Keys.ATLAS_PLACES_14, 'marko');
            case 'p8_to_abyss':
                return new ImDesc(Keys.ATLAS_POINTERS, 'pointer-left-canyon');
            case 'from_the_abyss':
                return new ImDesc(Keys.ATLAS_POINTERS_8, 'p8_pointer_terminal');
            case 'kill_skild':
                return new ImDesc(Keys.ATLAS_PLACES_15, 'skuld');

            case 'six_builders':
                return new ImDesc(Keys.ATLAS_PLACES_15, 'builder_2');

            case 'p8_to_fox':
                return new ImDesc(Keys.ATLAS_PORTALS, 'P3');

            case 'p8_force_cryst':
                return new ImDesc(Keys.ATLAS_PLACES_14, 'runecrystal_1');

            case 'p8_elem_worlds':
                return new ImDesc(Keys.ATLAS_PLACES_15, 'kult_1');

            case 'five_reapers':
                return new ImDesc(Keys.ATLAS_PLACES_15, 'reaper_1');
        }
        return new ImDesc(undefined, Keys.UI_WARNING);
    }

    private makeCallbackPropertyByTaskType(task: Task) {
        switch (task.taskType) {
            case TaskType.DAILY:
                this.awardReceivedText.setVisible(true);
                break;
            default:
                TaskPanel.PROCESSING_TASK_IDS.add(task.id);
                let loading = SpriteUtils.addSprite(this.scene!, this.completeButton.x / CU.S,  this.completeButton.y / CU.S - 10, Keys.LOADING_ANIM);
                loading.play(Keys.LOADING_ANIM);
                this.add(loading);
                break;
        }
    }

    private makeRibbon(task: Task) {
        switch (task.taskType) {
            case TaskType.DAILY:
                this.taskTypeBack.setVisible(true);
                SpriteUtils.setSprite(this.scene, this.taskTypeRibbon, Keys.ATLAS_UI, Keys.UI_DAILY_RIBBON).setVisible(true);
                this.taskTypeText.setText(Tr('ezhednevnoe')).setVisible(true);
                return;
            case TaskType.SCENARIO:
                this.taskTypeBack.setVisible(true);
                SpriteUtils.setSprite(this.scene, this.taskTypeRibbon, Keys.ATLAS_UI, Keys.UI_SCENARIO_RIBBON).setVisible(true);
                this.taskTypeText.setText(Tr('stsenarnoe')).setVisible(true);
                return;
            case TaskType.SINGLE:
                this.taskTypeBack.setVisible(true);
                SpriteUtils.setSprite(this.scene, this.taskTypeRibbon, Keys.ATLAS_UI, Keys.UI_SCENARIO_RIBBON).setVisible(true);
                this.taskTypeText.setText(Tr('sidequest')).setVisible(true);
                return;
        }

        if(TaskConstants.ALL_EVENT_TASKS.includes(task.taskId)){
            this.taskTypeBack.setVisible(true);
            SpriteUtils.setSprite(this.scene, this.taskTypeRibbon, Keys.ATLAS_UI, Keys.UI_SCENARIO_RIBBON).setVisible(true);
            this.taskTypeText.setText(Tr('event')).setVisible(true);
        }
    }
}

class ImDesc {
    atlasId: string | undefined;
    imageId!: string;

    constructor(atlasId: string | undefined, imageId: string) {
        this.atlasId = atlasId;
        this.imageId = imageId;
    }
}
